import { Camera } from "@mediapipe/camera_utils";
import { SelfieSegmentation } from "@mediapipe/selfie_segmentation";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";
import MicIcon from "@mui/icons-material/Mic";
import SettingsIcon from "@mui/icons-material/Settings";
import VideoCameraBackIcon from "@mui/icons-material/VideoCameraBack";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import EffectsPopup from "./effects-popup";

const VideoRecorder = ({ onClose, onSave }) => {
  const [recording, setRecording] = useState(false);
  const [videoBlob, setVideoBlob] = useState(null);
  const [devices, setDevices] = useState({ cameras: [], mics: [] });
  const [selectedCamera, setSelectedCamera] = useState("");
  const [selectedMic, setSelectedMic] = useState("");
  const [timer, setTimer] = useState(0);
  const [intervalId, setIntervalId] = useState(null);
  const [isStopRecording, setIsStopRecording] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [activeFilter, setActiveFilter] = useState("none");
  const [isStartButtonDisabled, setIsStartButtonDisabled] = useState(false); // New state for disabling button

  const videoRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunks = useRef([]);
  const canvasRef = useRef(null);
  const activeFilterRef = useRef(activeFilter);

  useEffect(() => {
    activeFilterRef.current = activeFilter;
  }, [activeFilter]);

  useEffect(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const selfieSegmentation = new SelfieSegmentation({
      locateFile: (file) =>
        `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${file}`,
    });

    selfieSegmentation.setOptions({ modelSelection: 1 });

    const blurMap = {
      none: "none",
      "light-blur": "blur(4px)",
      "strong-blur": "blur(10px)",
    };

    selfieSegmentation.onResults((results) => {
      const canvas = canvasRef.current;
      if (!canvas || !results || !results.image || !results.segmentationMask)
        return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      canvas.width = results.image.width;
      canvas.height = results.image.height;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      ctx.save();

      ctx.drawImage(
        results.segmentationMask,
        0,
        0,
        canvas.width,
        canvas.height
      );
      ctx.globalCompositeOperation = "source-in";
      ctx.drawImage(results.image, 0, 0, canvas.width, canvas.height);
      ctx.restore();

      ctx.globalCompositeOperation = "destination-over";
      ctx.filter = blurMap[activeFilter] || "none";
      ctx.drawImage(results.image, 0, 0, canvas.width, canvas.height);

      ctx.filter = "none";
    });

    const camera = new Camera(videoRef.current, {
      onFrame: async () => {
        await selfieSegmentation.send({ image: videoRef.current });
      },
      width: 640,
      height: 480,
    });

    camera.start();

    return () => {
      camera.stop();
    };
  }, [activeFilter]);

  useEffect(() => {
    const requestPermission = async () => {
      try {
        // Always request camera & mic permission first
        await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      } catch (err) {
        console.error("Permission denied ❌:", err);
        alert("Please allow camera and microphone access to continue.");
      }
    };

    const getDevices = async () => {
      try {
        const allDevices = await navigator.mediaDevices.enumerateDevices();
        const cameras = allDevices.filter(
          (device) => device.kind === "videoinput"
        );
        const mics = allDevices.filter(
          (device) => device.kind === "audioinput"
        );

        setDevices({ cameras, mics });

        if (cameras[0]) setSelectedCamera(cameras[0].deviceId);
        if (mics[0]) setSelectedMic(mics[0].deviceId);
      } catch (err) {
        console.error("Error fetching devices:", err);
      }
    };

    // Request permission first, then load devices
    requestPermission().then(() => {
      getDevices();
    });
  }, []);

  const startTimer = () => {
    setTimer(0);
    const id = setInterval(() => {
      setTimer((prev) => prev + 1);
    }, 1000);
    setIntervalId(id);
  };

  const stopTimer = () => {
    clearInterval(intervalId);
    setIntervalId(null);
  };

  const formatTime = (seconds) => {
    const min = String(Math.floor(seconds / 60)).padStart(2, "0");
    const sec = String(seconds % 60).padStart(2, "0");
    return `${min}:${sec}`;
  };

  const startPreview = async () => {
    if (!selectedCamera || !selectedMic) return;

    const stream = await navigator.mediaDevices.getUserMedia({
      video: { deviceId: selectedCamera },
      audio: false,
    });

    if (videoRef.current) {
      videoRef.current.srcObject = stream;
    }
  };

  useEffect(() => {
    if (selectedCamera) {
      startPreview();
    }
  }, [selectedCamera]);

  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { deviceId: selectedCamera },
      audio: { deviceId: selectedMic },
    });

    let finalStream;

    if (activeFilterRef.current !== "none") {
      const canvasStream = canvasRef.current.captureStream(30);
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        canvasStream.addTrack(audioTrack);
      }
      finalStream = canvasStream;
    } else {
      finalStream = stream;
    }

    mediaRecorderRef.current = new MediaRecorder(finalStream);
    recordedChunks.current = [];

    mediaRecorderRef.current.ondataavailable = (event) => {
      if (event.data.size > 0) recordedChunks.current.push(event.data);
    };

    mediaRecorderRef.current.onstop = () => {
      const blob = new Blob(recordedChunks.current, { type: "video/mp4" });
      setVideoBlob(blob);
      stopTimer();
      setIsStartButtonDisabled(false); // Re-enable button when recording stops
    };

    mediaRecorderRef.current.start();
    setRecording(true);
    setIsStopRecording(false);
    startTimer();
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
    setRecording(false);
    setIsStopRecording(true);
    stopTimer();
  };

  const handleSave = () => {
    if (videoBlob) {
      onSave(videoBlob);
      onClose();
    }
  };

  const handleRetake = async () => {
    setVideoBlob(null);
    setIsStopRecording(false);
    setTimer(0);

    // Restart preview before recording
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { deviceId: selectedCamera },
      audio: { deviceId: selectedMic },
    });

    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
    }

    // Now trigger countdown + recording
    handleStartClick();
  };

  useEffect(() => {
    let videoUrl;
    if (videoBlob) {
      videoUrl = URL.createObjectURL(videoBlob);
    }
    return () => {
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl);
      }
    };
  }, [videoBlob]);

  const handleStartClick = () => {
    setIsStartButtonDisabled(true); // Disable button when countdown starts
    setCountdown(3);
    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current);
          setCountdown(0);
          startRecording();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
  
  useEffect(() => {
    let videoUrl;
    if (videoBlob) {
      videoUrl = URL.createObjectURL(videoBlob);
    }
    return () => {
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl);
      }
    };
  }, [videoBlob]);

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ borderRadius: "20px" }}
    >
      <Box sx={{ bgcolor: "white", p: 3, borderRadius: 2, textAlign: "start" }}>
        <Stack direction={"row"} justifyContent={"space-between"}>
          <Typography fontSize={20} fontWeight={600}>
            Record Question Video
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
        {!isStopRecording ? (
          <>
            <Box
              sx={{
                width: "100%",
                height: 400,
                backgroundColor: "black",
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mt: 2,
                position: "relative",
              }}
            >
              {/* <video ref={videoRef} autoPlay style={{ width: "100%", height: "100%" }} /> */}

              <div
                style={{ position: "relative", width: "100%", height: "100%" }}
              >
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{
                    width: "100%",
                    height: "auto",
                    backgroundColor: "#000",
                    objectFit: "cover",
                  }}
                />
                <canvas
                  ref={canvasRef}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    zIndex: 10,
                    pointerEvents: "none",
                  }}
                />
              </div>

              {countdown > 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    backgroundColor: "rgba(0,0,0,0.7)",
                    padding: "20px 40px",
                    borderRadius: "10px",
                    fontSize: "48px",
                    color: "white",
                    fontWeight: "bold",
                    zIndex: 10,
                  }}
                >
                  {countdown}
                </Box>
              )}
              {recording && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 10,
                    left: 10,
                    backgroundColor: "rgba(255, 0, 0, 0.7)",
                    padding: "5px 10px",
                    borderRadius: "5px",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    fontSize: "14px",
                    zIndex: 20,
                  }}
                >
                  <AccessTimeIcon sx={{ fontSize: 16, mr: 1 }} />
                  {formatTime(timer)}
                </Box>
              )}

              <IconButton
                sx={{
                  position: "absolute",
                  bottom: 10,
                  right: 10,
                  color: "white",
                  fontSize: 15,
                  zIndex: 20, // 👈 Higher than canvas (which is zIndex: 10)
                }}
                onClick={() => setShowPopup(true)}
              >
                <SettingsIcon fontSize="small" /> Effects
              </IconButton>
              <EffectsPopup
                open={showPopup}
                onClose={() => setShowPopup(false)}
                activeFilter={activeFilter}
                onSelectEffect={(effect) => setActiveFilter(effect)}
              />
            </Box>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}
            >
              <Select
                size="small"
                value={selectedCamera}
                onChange={(e) => setSelectedCamera(e.target.value)}
                sx={{ width: "48%" }}
              >
                {devices.cameras.map((cam) => (
                  <MenuItem key={cam.deviceId} value={cam.deviceId}>
                    <VideoCameraBackIcon sx={{ mr: 1 }} />{" "}
                    {cam.label || "Camera"}
                  </MenuItem>
                ))}
              </Select>

              <Select
                size="small"
                value={selectedMic}
                onChange={(e) => setSelectedMic(e.target.value)}
                sx={{ width: "48%" }}
              >
                {devices.mics.map((mic) => (
                  <MenuItem key={mic.deviceId} value={mic.deviceId}>
                    <MicIcon sx={{ mr: 1 }} /> {mic.label || "Microphone"}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "80px",
              }}
            >
              {!recording ? (
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ borderRadius: "20px", textTransform: "none" }}
                  onClick={handleStartClick}
                  disabled={isStartButtonDisabled || countdown > 0} // Disable during countdown
                >
                  {countdown > 0 ? "Get ready..." : "Start Recording"}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{ borderRadius: "20px", textTransform: "none" }}
                  onClick={stopRecording}
                >
                  Stop Recording
                </Button>
              )}
            </Box>
          </>
        ) : (
          <>
            {videoBlob && videoBlob.size > 0 && (
              <video
                src={URL.createObjectURL(videoBlob)}
                controls
                style={{ width: "100%", marginTop: 10 }}
              />
            )}

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 2,
                alignItems: "center",
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                sx={{ borderRadius: "20px", textTransform: "none" }}
                onClick={handleSave}
              >
                Looks Good
              </Button>
              <Button
                variant="outlined"
                color="grey"
                sx={{ borderRadius: "20px", textTransform: "none" }}
                onClick={handleRetake}
              >
                Record again
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default VideoRecorder;